import { Fragment, type ReactNode } from 'react'
import { Alert } from '@/ui'
import { CitationCard } from './CitationCard'
import { TicketTable } from './TicketTable'
import { MessagesView } from './messages/MessagesView'
import { ReplySent } from './ReplySent'
import { TicketDraft } from './TicketDraft'
import { TicketCreated } from './TicketCreated'
import { FeedbackButtons } from './FeedbackButtons'
import { MessageBubble } from './MessageBubble'
import { ToolStatus } from './ToolStatus'
import { HighlightsCard } from './HighlightsCard'
import { TypingIndicator } from './TypingIndicator'
import { WidgetHost } from './widgets/WidgetHost'
import { WIDGET_REGISTRY } from './widgets/registry'
import { RequestedAttachment } from './RequestedAttachment'
import { SupportOffer } from './SupportOffer'
import { YesNoButtons, toYesNoAnswers } from './YesNoButtons'
import {
  AdviserRole,
  AdviserToolName,
  ToolInvocationState,
  TOOL_STATUS_CONFIG,
} from '../lib/constants'
import { parseListTicketsResult } from '../lib/parseListTickets'
import {
  parseTicketFileNames,
  parseToolOutput,
  toYesNoQuestion,
  type TicketFileNames,
} from '../lib/mappers'
import type {
  CitationSource,
  ToolInvocation,
  TicketSummary,
  FeedbackVote,
  WidgetDisplay,
} from '../lib/types'
import type { AttachmentRef } from '../lib/attachments.types'

const submitTicketPrompt = (subject: string, body: string): string =>
  `Submit the ticket with subject: ${subject}\n\nBody: ${body}`

const isHistorical = (invocation: ToolInvocation): boolean =>
  invocation.restored === true

const parseSupportOffer = (
  invocation: ToolInvocation
): { addsToOpenCase: boolean } | null => {
  const result = parseToolOutput(invocation, AdviserToolName.HandToSupport)
  if (!result || !('draft' in result)) return null
  return {
    addsToOpenCase:
      'addsToOpenCase' in result && result.addsToOpenCase === true,
  }
}

const isHandedOff = (invocation: ToolInvocation): boolean => {
  const result = parseToolOutput(invocation, AdviserToolName.HandToSupport)
  return result !== null && 'handedOff' in result
}

const isTicketSummary = (x: unknown): x is TicketSummary => {
  if (typeof x !== 'object' || x === null) return false
  return (
    'ticketId' in x &&
    typeof x.ticketId === 'number' &&
    'subject' in x &&
    typeof x.subject === 'string' &&
    'status' in x &&
    typeof x.status === 'string' &&
    'comments' in x &&
    Array.isArray(x.comments) &&
    'createdAt' in x &&
    typeof x.createdAt === 'string' &&
    'updatedAt' in x &&
    typeof x.updatedAt === 'string'
  )
}

const parseTicketResult = (result: unknown): TicketSummary[] => {
  try {
    const parsed = typeof result === 'string' ? JSON.parse(result) : result
    if (Array.isArray(parsed)) return parsed.filter(isTicketSummary)
    if (isTicketSummary(parsed)) return [parsed]
  } catch (err) {
    console.warn('[adviser] Failed to parse ticket result:', err)
  }
  return []
}

type CreateTicketResult =
  | { draft: true; subject: string; body: string }
  | ({
      success: true
      ticketId: number
      subject: string
      body: string
    } & TicketFileNames)
  | { error: string }

const isCreateTicketResult = (x: unknown): x is CreateTicketResult => {
  if (typeof x !== 'object' || x === null) return false
  return (
    ('draft' in x &&
      x.draft === true &&
      'subject' in x &&
      typeof x.subject === 'string' &&
      'body' in x &&
      typeof x.body === 'string') ||
    ('success' in x &&
      x.success === true &&
      'ticketId' in x &&
      typeof x.ticketId === 'number' &&
      'subject' in x &&
      typeof x.subject === 'string' &&
      'body' in x &&
      typeof x.body === 'string') ||
    ('error' in x && typeof x.error === 'string')
  )
}

export const parseCreateTicketResult = (
  result: unknown
): CreateTicketResult | null => {
  try {
    const parsed: unknown =
      typeof result === 'string' ? JSON.parse(result) : result
    if (!isCreateTicketResult(parsed)) return null
    if ('success' in parsed) {
      const { success, ticketId, subject, body } = parsed
      return {
        success,
        ticketId,
        subject,
        body,
        ...parseTicketFileNames(parsed),
      }
    }
    return parsed
  } catch (err) {
    console.warn('[adviser] Failed to parse create ticket result:', err)
    return null
  }
}

type AddTicketCommentResult = {
  success: true
  ticketId: number
  commentBody?: string
} & TicketFileNames

const isAddTicketCommentResult = (x: unknown): x is AddTicketCommentResult => {
  if (typeof x !== 'object' || x === null) return false
  if (!('success' in x) || x.success !== true) return false
  if (!('ticketId' in x) || typeof x.ticketId !== 'number') return false
  if (!('commentBody' in x)) return true
  return typeof x.commentBody === 'string' || x.commentBody === undefined
}

export const parseAddTicketCommentResult = (
  result: unknown
): AddTicketCommentResult | null => {
  try {
    const parsed: unknown =
      typeof result === 'string' ? JSON.parse(result) : result
    if (!isAddTicketCommentResult(parsed)) return null
    const { success, ticketId, commentBody } = parsed
    return {
      success,
      ticketId,
      commentBody,
      ...parseTicketFileNames(parsed),
    }
  } catch (err) {
    console.warn('[adviser] Failed to parse add_ticket_comment result:', err)
    return null
  }
}

interface RequestAttachmentResult {
  requestAttachment: true
  ticketId?: number
}

const isRequestAttachmentResult = (
  x: unknown
): x is RequestAttachmentResult => {
  if (typeof x !== 'object' || x === null) return false
  return 'requestAttachment' in x && x.requestAttachment === true
}

const parseRequestAttachmentResult = (
  result: unknown
): RequestAttachmentResult | null => {
  try {
    const parsed = typeof result === 'string' ? JSON.parse(result) : result
    return isRequestAttachmentResult(parsed) ? parsed : null
  } catch (err) {
    console.warn('[adviser] Failed to parse request_attachment result:', err)
    return null
  }
}

interface AssistantMessageContentProps {
  messageId: string
  toolInvocations?: ToolInvocation[]
  /** Text Haven wrote just before a widget or highlights card that is not the message's first, keyed by call id. */
  textBeforeWidget?: Readonly<Record<string, string>>
  /** What that turn cited, keyed by the same call id, so each answer carries its own sources. */
  sourcesBeforeWidget?: Readonly<Record<string, CitationSource[]>>
  trailing?: ReactNode
  /** The bubble drew text, so it is not showing its own indicator. */
  hasText?: boolean
  sources?: CitationSource[]
  isLatestDraft: boolean
  /** No later message exists, so the customer has not answered an offer in this one yet. */
  isLatestMessage?: boolean
  /** Only the newest call of each widget can be answered; older ones are locked. */
  latestWidgetCallIds?: ReadonlySet<string>
  renderedTools: Set<string>
  isStreaming?: boolean
  streamingActive?: boolean
  feedbackMap?: Record<string, FeedbackVote>
  onSend: (
    message: string,
    options?: {
      pendingAttachments?: AttachmentRef[]
      attachmentFiles?: File[]
    }
  ) => void
  onRetry?: () => void
  onFeedback?: (messageId: string, rating: -1 | 1, comment?: string) => void
  onWidgetOutcome?: (
    invocation: { toolCallId: string; toolName: string },
    text: string,
    display?: WidgetDisplay
  ) => Promise<boolean>
  /** Live flag value. A saved descriptor must not outlive the rollout. */
  messagesViewEnabled?: boolean
  /** The one list_tickets call in the transcript that renders a live list. */
  liveMessagesView?: ToolInvocation | null
}

/** A widget's sources, when it has a call id at all. */
const forCall = <T,>(
  byCallId: Readonly<Record<string, T>> | undefined,
  callId: string | undefined
): T | undefined => {
  if (!callId) return undefined
  return byCallId?.[callId]
}

export const AssistantMessageContent = ({
  messageId,
  toolInvocations,
  textBeforeWidget,
  sourcesBeforeWidget,
  trailing,
  hasText,
  sources,
  isLatestDraft,
  isLatestMessage = false,
  latestWidgetCallIds,
  renderedTools,
  isStreaming,
  streamingActive,
  feedbackMap,
  onSend,
  onRetry,
  onFeedback,
  onWidgetOutcome,
  messagesViewEnabled = false,
  liveMessagesView = null,
}: AssistantMessageContentProps) => (
  <>
    {toolInvocations?.map((invocation, index, invocations) => {
      const intro = invocation.toolCallId
        ? textBeforeWidget?.[invocation.toolCallId]
        : undefined
      const introSources = forCall(sourcesBeforeWidget, invocation.toolCallId)
      const introBubble = (intro || introSources) && (
        <>
          {intro && (
            <MessageBubble role={AdviserRole.Assistant} content={intro} />
          )}
          {introSources && introSources.length > 0 && (
            <CitationCard sources={introSources} />
          )}
        </>
      )

      if (invocation.toolName === AdviserToolName.ShowHighlights) {
        return (
          <Fragment key={invocation.toolCallId ?? invocation.toolName}>
            {introBubble}
            {invocation.state === ToolInvocationState.OutputAvailable && (
              <HighlightsCard result={invocation.result} onAsk={onSend} />
            )}
          </Fragment>
        )
      }

      // Kept out of the dedupe below: its key includes the result, so answering
      // a widget would change the key and redraw it mid-stream.
      if (WIDGET_REGISTRY[invocation.toolName] && onWidgetOutcome) {
        return (
          <Fragment key={invocation.toolCallId ?? invocation.toolName}>
            {introBubble}
            <WidgetHost
              invocation={invocation}
              disabled={streamingActive}
              superseded={
                latestWidgetCallIds !== undefined &&
                invocation.toolCallId !== undefined &&
                !latestWidgetCallIds.has(invocation.toolCallId)
              }
              onOutcome={onWidgetOutcome}
              onFollowUp={(text) => onSend(text)}
              streaming={Boolean(isStreaming)}
            />
          </Fragment>
        )
      }

      if (invocation.toolName === AdviserToolName.OfferYesNo) {
        const yesNo = parseToolOutput(invocation, AdviserToolName.OfferYesNo)
        const answers = yesNo ? toYesNoAnswers(yesNo) : []
        const question = yesNo ? toYesNoQuestion(yesNo) : ''
        return (
          <Fragment key={invocation.toolCallId ?? invocation.toolName}>
            {introBubble}
            {question && (
              <MessageBubble role={AdviserRole.Assistant} content={question} />
            )}
            {isLatestMessage && !streamingActive && answers.length > 0 && (
              <YesNoButtons answers={answers} onSend={onSend} />
            )}
          </Fragment>
        )
      }

      const supportOffer = parseSupportOffer(invocation)
      if (supportOffer) {
        const accepted = invocations.slice(index + 1).some(isHandedOff)
        return isLatestMessage && !accepted ? (
          <SupportOffer
            key={invocation.toolCallId ?? invocation.toolName}
            addsToOpenCase={supportOffer.addsToOpenCase}
            disabled={Boolean(isStreaming)}
            onSend={onSend}
          />
        ) : null
      }

      const toolKey = `${invocation.toolName}-${JSON.stringify(invocation.result ?? '')}`
      if (renderedTools.has(toolKey)) return null

      if (TOOL_STATUS_CONFIG[invocation.toolName]) {
        return (
          <ToolStatus
            key={`${invocation.toolName}-${invocation.state}`}
            invocation={invocation}
          />
        )
      }

      if (streamingActive) return null

      if (
        invocation.toolName === AdviserToolName.ListTickets &&
        invocation.state === ToolInvocationState.OutputAvailable
      ) {
        const outcome = parseListTicketsResult(invocation.result)
        // A conversation saved while the flag was on still carries a
        // descriptor. Without this check, turning the flag off would leave
        // the new experience live for anyone who had already seen it.
        if (outcome && messagesViewEnabled) {
          if (invocation !== liveMessagesView) return null
          renderedTools.add(toolKey)
          if (outcome.kind === 'failed') {
            return <MessagesView key={toolKey} />
          }
          return (
            <MessagesView
              key={toolKey}
              initialTab={outcome.tab}
              initialQuery={outcome.query ?? undefined}
              initialCounts={outcome.counts ?? undefined}
            />
          )
        }

        const legacyTickets = parseTicketResult(invocation.result)
        if (legacyTickets.length > 0) {
          renderedTools.add(toolKey)
          return (
            <TicketTable
              key={toolKey}
              tickets={legacyTickets}
              onSend={onSend}
            />
          )
        }
        return null
      }

      if (
        invocation.toolName === AdviserToolName.GetTicketDetails &&
        invocation.state === ToolInvocationState.OutputAvailable
      ) {
        const tickets = parseTicketResult(invocation.result)
        if (tickets.length > 0) {
          renderedTools.add(toolKey)
          return <TicketTable key={toolKey} tickets={tickets} onSend={onSend} />
        }
      }
      if (
        invocation.toolName === AdviserToolName.AddTicketComment &&
        invocation.state === ToolInvocationState.OutputAvailable
      ) {
        const parsed = parseAddTicketCommentResult(invocation.result)
        if (parsed !== null) {
          renderedTools.add(toolKey)
          return (
            <Fragment key={toolKey}>
              <ReplySent
                body={parsed.commentBody}
                attachedFiles={parsed.attachedFiles}
                droppedAttachments={parsed.droppedAttachments}
              />
              {messagesViewEnabled && invocation === liveMessagesView && (
                <MessagesView
                  restoreTab
                  postedReplyTicketId={
                    isHistorical(invocation) ? undefined : parsed.ticketId
                  }
                />
              )}
            </Fragment>
          )
        }
      }
      if (
        invocation.toolName === AdviserToolName.RequestAttachment &&
        invocation.state === ToolInvocationState.OutputAvailable
      ) {
        const parsed = parseRequestAttachmentResult(invocation.result)
        if (parsed === null) return null
        if (!messagesViewEnabled) return null
        // The widget is only an upload surface, so a replayed one has nothing
        // to record and nowhere to send what it collects.
        if (isHistorical(invocation)) return null
        renderedTools.add(toolKey)

        return (
          <RequestedAttachment
            key={toolKey}
            disabled={Boolean(isStreaming)}
            onSend={onSend}
          />
        )
      }
      if (
        invocation.toolName === AdviserToolName.CreateTicket &&
        invocation.state === ToolInvocationState.OutputAvailable
      ) {
        const parsed = parseCreateTicketResult(invocation.result)
        if (parsed === null) return null
        renderedTools.add(toolKey)

        if ('error' in parsed) {
          return (
            <Alert
              key={toolKey}
              variant="error"
              description={parsed.error}
              ctaText={onRetry ? 'Try again' : undefined}
              ctaOnClick={onRetry}
            />
          )
        }

        if ('draft' in parsed) {
          const historical = messagesViewEnabled && isHistorical(invocation)
          return (
            <TicketDraft
              key={toolKey}
              subject={parsed.subject}
              body={parsed.body}
              disabled={historical || !isLatestDraft || Boolean(isStreaming)}
              readOnly={historical}
              attachmentsEnabled={messagesViewEnabled}
              onSubmit={({
                subject,
                body,
                pendingAttachments,
                attachmentFiles,
              }) =>
                onSend(submitTicketPrompt(subject, body), {
                  pendingAttachments,
                  attachmentFiles,
                })
              }
              onCancel={() => onSend("Never mind, don't submit the ticket")}
            />
          )
        }

        return (
          <TicketCreated
            key={toolKey}
            ticketId={parsed.ticketId}
            subject={parsed.subject}
            attachedFiles={parsed.attachedFiles}
            droppedAttachments={parsed.droppedAttachments}
            onSend={onSend}
          />
        )
      }
      return null
    })}
    {trailing}
    {streamingActive &&
      hasText &&
      !trailing &&
      toolInvocations?.some(
        (invocation) => WIDGET_REGISTRY[invocation.toolName]
      ) && (
        <div className="flex items-start">
          <TypingIndicator />
        </div>
      )}
    {!streamingActive && sources && sources.length > 0 && (
      <CitationCard sources={sources} />
    )}
    {!streamingActive && onFeedback && (
      <FeedbackButtons
        initialRating={feedbackMap?.[messageId]?.rating}
        onSubmit={(rating, comment) => onFeedback(messageId, rating, comment)}
      />
    )}
  </>
)
